import React, { MutableRefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";
import { GuestUser } from "@scrile/api-provider/dist/api/GuestUsersProvider";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { Routes } from "router/config";
import { useTranslation } from "locales";
import useAuthUser from "hooks/useAuthUser";
import { formatCallDurationLocalized } from "lib/dateLocale";
import { SYSTEM_MESSAGE_PREFIX } from "lib/emitter";
import { getMessageMetadata } from "lib/messageMetadata";
import { useAuthedFileUrl } from "lib/recordingFileAuth";
import { filenameFromUrl, shareFileFromUrl, shouldInterceptDownload } from "lib/shareFile";
import { localizeSystemTextFragment } from "lib/systemMessageText";
import DateSeparator from "components/DateSeparator";
import MessageBubble from "components/MessageBubble";
import RecordingTranscriptPanel from "components/RecordingTranscriptPanel";
import ScrollToBottomButton from "components/ScrollToBottomButton";
import { getMessageItem } from "./components/MessageItem";
import "./style.scss";

const INSIGHTS_MARKER_RE = /\[\[insights:([a-zA-Z0-9_-]+)\]\]/;
const RECORDING_FILE_RE = /\/recordings\/files\/([a-zA-Z0-9_-]+)(?:\.[a-zA-Z0-9]+)?/;

// Module-scope so Virtuoso gets a stable component identity across renders.
function ListBottomSpacer() {
  return <div style={{ height: 12 }} />;
}
const LIST_COMPONENTS = { Footer: ListBottomSpacer };

function extractRecordingIdFromUrl(url: string): string | null {
  const m = url.match(RECORDING_FILE_RE);
  return m ? m[1] : null;
}

/**
 * Derive the poster-thumbnail URL from a recording URL. ffmpeg writes
 * `<fileId>.jpg` alongside the `<fileId>.webm` at upload, so we just
 * swap the extension while preserving any query string (`?token=...`).
 * Returns `null` if the URL doesn't look like a recording file URL,
 * in which case callers skip the poster attribute.
 */
function deriveThumbnailUrl(videoUrl: string): string | null {
  if (!/\/recordings\/files\/[^?#]+\.(webm|mp4)(?=$|[?#])/i.test(videoUrl)) return null;
  return videoUrl.replace(/\.(webm|mp4)(?=$|[?#])/i, ".jpg");
}

interface InsightsTrigger {
  onOpenInsights?: (recordingId: string) => void;
}

function RecordingLink({ url, onOpenInsights }: { url: string } & InsightsTrigger) {
  const { t } = useTranslation();
  const [showPlayer, setShowPlayer] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const recordingId = extractRecordingIdFromUrl(url);
  const canOpenInsights = !!(onOpenInsights && recordingId);

  // On mobile (Capacitor WKWebView) the native media loader does not
  // include the SESSIONID cookie or Authorization header when fetching
  // the .webm — see lib/recordingFileAuth.ts. On web this is a no-op.
  const playableUrl = useAuthedFileUrl(url);
  // Poster thumbnail (`<fileId>.jpg` extracted by ffmpeg at upload).
  // Old recordings without a generated thumbnail return 404 here; the
  // browser silently drops the poster and the tile falls back to its
  // dark background color — no UI break.
  const thumbnailUrl = useAuthedFileUrl(deriveThumbnailUrl(url));

  const openInsights = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (onOpenInsights && recordingId) onOpenInsights(recordingId);
  };

  const downloadRecording = (e: React.MouseEvent) => {
    if (!shouldInterceptDownload()) return; // web: let <a download> fire
    e.preventDefault();
    void shareFileFromUrl(playableUrl, {
      filename: filenameFromUrl(url, "recording.webm"),
      mimeType: "video/webm",
      title: "Recording",
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[recording] download/share failed", err);
    });
  };

  return (
    <>
      {!showPlayer ? (
        <div
          className="messages-list-view__recording-preview"
          onClick={() => setShowPlayer(true)}
          role="button"
          tabIndex={0}
          style={thumbnailUrl ? { backgroundImage: `url(${thumbnailUrl})` } : undefined}
        >
          <div className="messages-list-view__recording-play-btn">
            <svg viewBox="0 0 24 24" width="32" height="32">
              <circle cx="12" cy="12" r="11" fill="rgba(0,0,0,0.5)" />
              <path d="M9.5 7.5v9l7-4.5z" fill="#fff" />
            </svg>
          </div>
          <div className="messages-list-view__recording-label">
            <svg viewBox="0 0 24 24" width="14" height="14">
              <path
                d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"
                fill="currentColor"
              />
            </svg>
            {t("Recording")}
          </div>
          {canOpenInsights && (
            <button
              className="messages-list-view__recording-insights-btn"
              onClick={openInsights}
              type="button"
              title={t("AI meeting insights")}
            >
              ✦ {t("Insights")}
            </button>
          )}
        </div>
      ) : (
        <div className="messages-list-view__recording-inline">
          <video
            src={playableUrl}
            poster={thumbnailUrl || undefined}
            controls
            preload="metadata"
            className="messages-list-view__recording-video"
          />
          <div className="messages-list-view__recording-actions">
            {canOpenInsights && (
              <button
                className="messages-list-view__recording-insights-btn messages-list-view__recording-insights-btn--inline"
                onClick={openInsights}
                type="button"
                title={t("AI meeting insights")}
              >
                ✦
              </button>
            )}
            <button
              className="messages-list-view__recording-fullscreen-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowFullscreen(true);
              }}
              type="button"
              title={t("Fullscreen")}
            >
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path
                  d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <a
              href={playableUrl}
              download
              onClick={downloadRecording}
              className="messages-list-view__recording-download-btn"
              title={t("Download")}
            >
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor" />
              </svg>
            </a>
          </div>
        </div>
      )}
      {showFullscreen && (
        <div className="messages-list-view__video-overlay" onClick={() => setShowFullscreen(false)}>
          <div className="messages-list-view__video-container" onClick={(e) => e.stopPropagation()}>
            <button className="messages-list-view__video-close" onClick={() => setShowFullscreen(false)}>
              &times;
            </button>
            {/* No autoPlay: server-concatenated webm recordings can lack a
                seekable header (no cues/duration) at open, and autoplaying from
                an unstable header garbled fullscreen playback. Let the user
                press play once the container is fully parsed. */}
            <video
              src={playableUrl}
              poster={thumbnailUrl || undefined}
              controls
              playsInline
              className="messages-list-view__video-player"
            />
            <a href={playableUrl} download onClick={downloadRecording} className="messages-list-view__video-download">
              {t("Download recording")}
            </a>
          </div>
        </div>
      )}
    </>
  );
}

const TRANSCRIPT_URL_RE = /\/recordings\/[a-zA-Z0-9_-]+\/transcript\.(txt|srt)(?:\?[^\s]*)?$/;

/**
 * Download link for a transcript file embedded in a system message.
 *
 * On web this is a plain `<a download>` — the browser saves the file.
 * On mobile Capacitor it intercepts the click and routes through
 * `downloadOrShareFile` so the share sheet appears instead of an
 * external Safari window (which is what `<a href download>` triggers
 * inside WKWebView). The authed-URL hook adds the `?token=` query
 * param on mobile so the fetch passes the backend's `requireAuth`.
 */
function TranscriptDownloadLink({ url, ext }: { url: string; ext: string }) {
  const { t } = useTranslation();
  const authedUrl = useAuthedFileUrl(url);
  const onClick = (e: React.MouseEvent) => {
    if (!shouldInterceptDownload()) return; // web: let <a download> fire
    e.preventDefault();
    void shareFileFromUrl(authedUrl, {
      filename: filenameFromUrl(url, `transcript.${ext}`),
      mimeType: ext === "srt" ? "application/x-subrip" : "text/plain",
      title: "Transcript",
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[transcript] download/share failed", err);
    });
  };
  return (
    <a
      href={authedUrl}
      download
      onClick={onClick}
      className="messages-list-view__system-link messages-list-view__system-link--download"
    >
      {t("Download transcript (.{{ext}})", { ext })}
    </a>
  );
}

// Shared localizer: lib/systemMessageText.ts (also used by chat-list and
// topics previews so every surface renders system text identically).

function extractInsightsRecordingId(text: string): string | null {
  const m = text.match(INSIGHTS_MARKER_RE);
  return m ? m[1] : null;
}

function stripInsightsMarker(text: string): string {
  return text.replace(INSIGHTS_MARKER_RE, "").replace(/\s+$/, "");
}

function renderSystemMessageText(
  text: string,
  opts: InsightsTrigger = {},
  tFn: (key: string) => string = (k) => k
): React.ReactNode {
  const insightsId = extractInsightsRecordingId(text);
  const clean = insightsId ? stripInsightsMarker(text) : text;

  const parts = clean.split(/(https?:\/\/[^\s]+)/g);
  const rendered: React.ReactNode[] = parts.map((part, i) => {
    if (!/^https?:\/\//.test(part)) {
      // Plain-text fragment between URLs — run through localizer so well-known
      // server-side templates ("Recording started", "Transcript ready (N segments):",
      // "Video call ended (duration: 48s)", etc.) render in the user's language.
      return <React.Fragment key={`t-${i}`}>{localizeSystemTextFragment(part, tFn)}</React.Fragment>;
    }
    // Inline player for conference recording video
    if (/\/recordings\/files\//.test(part)) {
      return <RecordingLink key={`r-${i}`} url={part} onOpenInsights={opts.onOpenInsights} />;
    }
    // Transcript download — show concise labelled link instead of full URL
    const transcriptMatch = part.match(TRANSCRIPT_URL_RE);
    if (transcriptMatch) {
      const ext = transcriptMatch[1].toLowerCase();
      return <TranscriptDownloadLink key={`d-${i}`} url={part} ext={ext} />;
    }
    return (
      <a
        key={`l-${i}`}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="messages-list-view__system-link"
      >
        {part}
      </a>
    );
  });

  if (insightsId && opts.onOpenInsights) {
    rendered.push(
      <button
        key="insights-btn"
        type="button"
        className="messages-list-view__system-insights-btn"
        onClick={() => opts.onOpenInsights?.(insightsId)}
      >
        {tFn("Open insights")}
      </button>
    );
  }

  return rendered;
}

type SystemMessageType =
  | "call-start"
  | "call-end"
  | "call-cancelled"
  | "recording"
  | "transcript"
  | "insights"
  | "member-change"
  | "default";

function getSystemMessageType(text: string): SystemMessageType {
  const lower = text.toLowerCase();
  if (lower.includes("video call started") || lower.includes("call started")) return "call-start";
  if (lower.includes("video call ended") || lower.includes("call ended")) return "call-end";
  if (lower.includes("call cancelled") || lower.includes("call missed") || lower.includes("missed call"))
    return "call-cancelled";
  if (lower.includes("meeting insights") || INSIGHTS_MARKER_RE.test(text)) return "insights";
  if (lower.includes("transcript")) return "transcript";
  if (lower.includes("recording")) return "recording";
  if (
    lower.includes("joined") ||
    lower.includes("left") ||
    lower.includes("added") ||
    lower.includes("removed") ||
    lower.includes("created the group")
  )
    return "member-change";
  return "default";
}

/** Try to parse a 1-on-1 call JSON message: {"type":"call","id":...,"status":"ENDED"} */
interface CallMessageData {
  type: "call";
  id: number;
  callerId: number;
  receiverId: number;
  status: string;
  startTime?: string;
  endTime?: string;
}

function tryParseCallMessage(text: string): CallMessageData | null {
  if (!text || text.startsWith(SYSTEM_MESSAGE_PREFIX)) return null;
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") || (!trimmed.includes('"type":"call"') && !trimmed.includes('"type": "call"')))
    return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && parsed.type === "call" && parsed.status) return parsed as CallMessageData;
  } catch {
    /* not JSON */
  }
  return null;
}

function formatCallDuration(startTime?: string, endTime?: string): string | null {
  if (!startTime || !endTime) return null;
  const ms = new Date(endTime).getTime() - new Date(startTime).getTime();
  if (ms < 0 || isNaN(ms)) return null;
  return formatCallDurationLocalized(Math.round(ms / 1000));
}

function getCallSystemType(status: string): SystemMessageType {
  switch (status) {
    case "ENDED":
      return "call-end";
    case "CANCELLED":
    case "DECLINED":
      return "call-cancelled";
    default:
      return "call-start";
  }
}

function getCallDisplayText(
  callData: CallMessageData,
  authUserId: string | undefined,
  tFn: (key: string, vars?: Record<string, string | number>) => string
): string {
  const isCaller = String(callData.callerId) === String(authUserId);
  const duration = formatCallDuration(callData.startTime, callData.endTime);

  switch (callData.status) {
    case "ENDED":
      return duration ? tFn("Call ended ({{duration}})", { duration }) : tFn("Call ended");
    case "CANCELLED":
      return isCaller ? tFn("Cancelled call") : tFn("Missed call");
    case "DECLINED":
      return isCaller ? tFn("Call declined") : tFn("Declined call");
    default:
      return tFn("Call");
  }
}

function SystemMessageIcon({ type }: { type: SystemMessageType }) {
  switch (type) {
    case "call-start":
      return (
        <svg className="messages-list-view__system-icon" viewBox="0 0 24 24" width="16" height="16">
          <path
            d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"
            fill="currentColor"
          />
        </svg>
      );
    case "call-end":
      return (
        <svg className="messages-list-view__system-icon" viewBox="0 0 24 24" width="16" height="16">
          <path
            d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 010-1.36C3.69 8.68 7.62 7 12 7s8.31 1.68 11.71 4.72c.18.18.29.44.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28a11.27 11.27 0 00-2.67-1.85.996.996 0 01-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"
            fill="currentColor"
          />
        </svg>
      );
    case "call-cancelled":
      return (
        <svg className="messages-list-view__system-icon" viewBox="0 0 24 24" width="16" height="16">
          <path
            d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
            fill="currentColor"
          />
          <path d="M19 3L5 17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
        </svg>
      );
    case "recording":
      return (
        <svg className="messages-list-view__system-icon" viewBox="0 0 24 24" width="16" height="16">
          <path
            d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"
            fill="currentColor"
          />
        </svg>
      );
    case "transcript":
      return (
        <svg className="messages-list-view__system-icon" viewBox="0 0 24 24" width="16" height="16">
          <path
            d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"
            fill="currentColor"
          />
        </svg>
      );
    case "insights":
      return (
        <svg className="messages-list-view__system-icon" viewBox="0 0 24 24" width="16" height="16">
          <path
            d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"
            fill="none"
          />
          <path
            d="M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2zm2 14h2v-6H7v6zm4 0h2V7h-2v10zm4 0h2v-4h-2v4z"
            fill="currentColor"
          />
        </svg>
      );
    case "member-change":
      return (
        <svg className="messages-list-view__system-icon" viewBox="0 0 24 24" width="16" height="16">
          <path
            d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
            fill="currentColor"
          />
        </svg>
      );
    default:
      return (
        <svg className="messages-list-view__system-icon" viewBox="0 0 24 24" width="16" height="16">
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
            fill="currentColor"
          />
        </svg>
      );
  }
}

function SystemMessageCard({
  text,
  type,
  activeCallUuid,
  isLastCallStart,
  onOpenInsights,
}: {
  text: string;
  type: SystemMessageType;
  activeCallUuid?: string | null;
  isLastCallStart?: boolean;
  onOpenInsights?: (recordingId: string) => void;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const showJoin = type === "call-start" && activeCallUuid && isLastCallStart;
  const cls = ["messages-list-view__system-message", `__${type}`];

  const handleJoinCall = () => {
    if (activeCallUuid) {
      history.push(generatePath(Routes.conferenceJoinByConferenceUuid, { conferenceUuid: activeCallUuid }));
    }
  };

  return (
    <div className={cls.join(" ")}>
      <span className="messages-list-view__system-card">
        <SystemMessageIcon type={type} />
        <span className="messages-list-view__system-text">{renderSystemMessageText(text, { onOpenInsights }, t)}</span>
        {showJoin && (
          <button className="messages-list-view__system-join" onClick={handleJoinCall} type="button">
            {t("Join")}
          </button>
        )}
      </span>
    </div>
  );
}

function InsightsModal({ recordingId, onClose }: { recordingId: string; onClose: () => void }) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="messages-list-view__insights-overlay" onClick={onClose}>
      <div
        className="messages-list-view__insights-container"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button className="messages-list-view__insights-close" onClick={onClose} type="button" aria-label={t("Close")}>
          ×
        </button>
        <RecordingTranscriptPanel recordingId={recordingId} />
      </div>
    </div>
  );
}

interface Props {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  scrollToBottomRef?: MutableRefObject<(() => void) | null>;
  /**
   * Parent calls this on cache→fresh phase swap (after the network fetch
   * lands and replaces the cached message list). Pins the viewport to the
   * absolute bottom AND keeps it pinned for ~1.5 s while async height
   * changes from images / link previews / voice metadata are settling.
   * Without this signal the watcher would only re-anchor once per resize
   * event and the user would drift slightly above bottom as the last
   * async load lands after polling's 1 s window has already exited.
   */
  settleAtBottomRef?: MutableRefObject<(() => void) | null>;
  messages: Message[];
  infiniteScrollEnabled: boolean;
  className?: string;
  messageItemClass?: string;
  loadMore?: (cb: () => void) => void;
  messageObserve?: (ref: HTMLDivElement | null) => void;
  showUnread?: boolean;
  minUnreadMessageId?: string;
  avatarSize: number;
  showScreenName: boolean;
  isLiveChat?: boolean;
  userNames?: Record<string, string>;
  guestUser?: GuestUser | null;
  onMessageUpdated?: (msg: Message) => void;
  onMessageDeleted?: (messageId: string) => void;
  highlightMessageId?: string;
  opponentMinUnreadMessageId?: string;
  hasMention?: boolean;
  onMentionClick?: () => void;
  searchQuery?: string;
  onReply?: (message: Message) => void;
  onForward?: (message: Message) => void;
  onPin?: (message: Message) => void;
  onOpenThread?: (message: Message) => void;
  // Reply-quote click → parent pages history until the quoted message is
  // loaded, then highlights it (virtualized list — the target's DOM node
  // usually doesn't exist, so scrollIntoView can't work).
  onQuoteClick?: (messageId: string) => void;
  threadReplyCounts?: Map<string, number>;
  pinnedMessageIds?: Set<string>;
  activeCallUuid?: string | null;
  // Moderator capability forwarded to MessageBubble — when true, the
  // Delete action appears on messages authored by other users too
  // (group / channel owner).
  canModerate?: boolean;
  // --- Multi-select ---
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelection?: (message: Message) => void;
  onEnterSelection?: (message: Message) => void;
}

const diffMinutes = (startDate: number | string | Date, endDate: number | string | Date) => {
  const d1 = new Date(startDate);
  const d2 = new Date(endDate);
  if (d1.toDateString() !== d2.toDateString()) return;
  d1.setSeconds(0);
  d2.setSeconds(0);
  return Math.round((((d1.getTime() - d2.getTime()) % 86400000) % 3600000) / 60000);
};

const isSameDay = (d1: string, d2: string) => {
  return new Date(d1).toDateString() === new Date(d2).toDateString();
};

function MessagesListView({
  containerRef,
  scrollToBottomRef,
  settleAtBottomRef,
  messages,
  infiniteScrollEnabled,
  className,
  messageItemClass,
  loadMore,
  messageObserve,
  showUnread,
  minUnreadMessageId,
  showScreenName,
  avatarSize,
  isLiveChat,
  userNames,
  guestUser,
  onMessageUpdated,
  onMessageDeleted,
  highlightMessageId,
  opponentMinUnreadMessageId,
  hasMention,
  onMentionClick,
  searchQuery,
  onReply,
  onForward,
  onPin,
  onOpenThread,
  onQuoteClick,
  threadReplyCounts,
  pinnedMessageIds,
  activeCallUuid,
  canModerate,
  selectionMode,
  selectedIds,
  onToggleSelection,
  onEnterSelection,
}: Props) {
  const { t } = useTranslation();
  const { user } = useAuthUser();
  // Filter out thread replies — they should only appear in ThreadPanel, not in the main chat
  const visibleMessages = useMemo(() => {
    return messages.filter((m) => {
      const meta = getMessageMetadata(m);
      return !meta?.threadParentId;
    });
  }, [messages]);

  const unreadCount = useMemo(() => {
    if (!minUnreadMessageId || minUnreadMessageId === "0") return 0;
    const idx = visibleMessages.findIndex((m) => m.id === minUnreadMessageId);
    return idx >= 0 ? idx + 1 : 0;
  }, [visibleMessages, minUnreadMessageId]);

  const lastCallStartId = useMemo(() => {
    for (const m of visibleMessages) {
      if (m.text?.startsWith(SYSTEM_MESSAGE_PREFIX)) {
        const stripped = m.text.replace(SYSTEM_MESSAGE_PREFIX, "").trim();
        if (getSystemMessageType(stripped) === "call-start") return m.id;
      }
    }
    return undefined;
  }, [visibleMessages]);

  const cls = ["messages-list-view"];
  if (className) cls.push(className);
  if (!isLiveChat) cls.push("__messenger");

  // Build flat item list for Virtuoso: date separators, system messages, bubbles, unread markers
  type VListItem =
    | { type: "date"; date: string; key: string }
    | { type: "unread"; key: string }
    | {
        type: "system";
        text: string;
        systemType: SystemMessageType;
        activeCallUuid?: string | null;
        isLastCallStart?: boolean;
        key: string;
      }
    | { type: "livechat"; message: Message; startMessage: boolean; endMessage: boolean; key: string }
    | {
        type: "bubble";
        message: Message;
        isOwn: boolean;
        startMessage: boolean;
        endMessage: boolean;
        isHighlighted: boolean;
        key: string;
      };

  const virtualItems = useMemo<VListItem[]>(() => {
    const items: VListItem[] = [];
    // Virtuoso's followOutput requires newest item at the END (highest index).
    // API returns DESC (newest first), so we reverse to ASC for correct scroll behaviour.
    const arr = [...visibleMessages].reverse();

    for (let index = 0; index < arr.length; index++) {
      const message = arr[index];
      const previousMessage = arr[index - 1]; // older (lower index in ASC)
      const nextMessage = arr[index + 1]; // newer (higher index in ASC)
      const splitNewMessage = showUnread && minUnreadMessageId === message.id;

      let startMessage = true;
      let endMessage = true;

      if (previousMessage) {
        const td = diffMinutes(message.time, previousMessage.time);
        if (
          previousMessage.senderUser?.id === message.senderUser?.id &&
          previousMessage.link?.subject.subjectId === message.link?.subject.subjectId &&
          td === 0
        )
          startMessage = false;
      }

      if (nextMessage) {
        const td = diffMinutes(nextMessage.time, message.time);
        if (
          nextMessage.senderUser?.id === message.senderUser?.id &&
          nextMessage.link?.subject.subjectId === message.link?.subject.subjectId &&
          td === 0
        )
          endMessage = false;
      }

      if (splitNewMessage) startMessage = true;

      // Date separator
      const showDateSep = !isLiveChat && (!previousMessage || !isSameDay(message.time, previousMessage.time));
      if (showDateSep) {
        items.push({ type: "date", date: message.time, key: `date-${message.time}` });
      }

      // Unread marker goes ABOVE the first unread message
      if (splitNewMessage) {
        items.push({ type: "unread", key: `unread-${message.id}` });
      }

      if (isLiveChat) {
        items.push({ type: "livechat", message, startMessage, endMessage, key: `lc-${message.id}` });
      } else {
        const isSystemMessage = message.text?.startsWith(SYSTEM_MESSAGE_PREFIX);
        const strippedText = isSystemMessage ? message.text.replace(SYSTEM_MESSAGE_PREFIX, "").trim() : "";
        const systemType = isSystemMessage ? getSystemMessageType(strippedText) : undefined;
        const callData = !isSystemMessage ? tryParseCallMessage(message.text) : null;
        const isOwn = message.senderId === user?.id;
        const isHighlighted = highlightMessageId === message.id;

        if (callData) {
          items.push({
            type: "system",
            text: getCallDisplayText(callData, user?.id, t),
            systemType: getCallSystemType(callData.status),
            key: `sys-${message.id}`,
          });
        } else if (isSystemMessage && systemType) {
          items.push({
            type: "system",
            text: strippedText,
            systemType,
            activeCallUuid,
            isLastCallStart: message.id === lastCallStartId,
            key: `sys-${message.id}`,
          });
        } else {
          // Key prefers `_optimisticId` when present so the React key is the
          // same before and after the optimistic→real server swap. Without
          // this, the bubble remounts (id changes from "_opt_*" to the real
          // numeric id), Virtuoso re-measures from the 80px estimate, and
          // the user's just-sent message drifts up out of view.
          const stableKey = (message as { _optimisticId?: string })._optimisticId || message.id;
          items.push({
            type: "bubble",
            message,
            isOwn,
            startMessage,
            endMessage,
            isHighlighted,
            key: `msg-${stableKey}`,
          });
        }
      }
    }
    return items;
  }, [
    visibleMessages,
    showUnread,
    minUnreadMessageId,
    isLiveChat,
    highlightMessageId,
    user?.id,
    lastCallStartId,
    activeCallUuid,
  ]);

  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const [atBottom, setAtBottom] = useState(true);
  const [insightsRecordingId, setInsightsRecordingId] = useState<string | null>(null);
  const handleOpenInsights = useCallback((id: string) => setInsightsRecordingId(id), []);
  const handleCloseInsights = useCallback(() => setInsightsRecordingId(null), []);

  const virtualItemsLenRef = useRef(virtualItems.length);
  virtualItemsLenRef.current = virtualItems.length;

  // --- Virtuoso prepend protocol (load older messages on scroll-up) ---
  // Older pages are PREPENDED to the front of `virtualItems`. Virtuoso can only
  // keep the viewport anchored AND re-arm `startReached` if we tell it how many
  // items appeared above the top, via `firstItemIndex` (decremented per prepend).
  // Without this, `startReached` fires once and never again — history loading
  // dies after the first extra page. See MESSAGES_SCROLL_FLOW.md.
  //
  // We anchor on the first MESSAGE ROW (stable `msg-`/`lc-` key), NOT on
  // virtualItems[0]: in messenger mode index 0 is a date separator whose key is
  // `date-<oldest-of-day time>`, which CHANGES when older same-day messages are
  // prepended — anchoring on it would misfire. Decrementing `firstItemIndex` by
  // the anchor's index delta keeps that row (and the viewport) put, in VListItem
  // units, so a freshly-inserted older-day separator is counted automatically.
  // Appends at the bottom (new messages) leave the top row unchanged, so
  // `firstItemIndex` is untouched and `followOutput` keeps working. When the
  // list empties or the anchor disappears (chat/topic switch) we re-base — no
  // remount needed, which preserves the cache-first zero-flash open.
  const START_INDEX = 100_000;
  const [firstItemIndex, setFirstItemIndex] = useState(START_INDEX);
  const prevAnchorRef = useRef<{ key: string; index: number } | null>(null);
  useLayoutEffect(() => {
    // Anchor on the first message-backed row (bubble / livechat / system — all
    // keyed by a stable message id). Skip the date separator (volatile key) and
    // the unread marker.
    let key: string | null = null;
    let index = -1;
    for (let i = 0; i < virtualItems.length; i++) {
      const it = virtualItems[i];
      if (it.type === "bubble" || it.type === "livechat" || it.type === "system") {
        key = it.key;
        index = i;
        break;
      }
    }
    const prev = prevAnchorRef.current;
    prevAnchorRef.current = key !== null ? { key, index } : null;

    if (key === null) {
      setFirstItemIndex(START_INDEX); // emptied (rare while mounted — guard usually unmounts) — re-base
      return;
    }
    if (!prev) return; // first content paint — base stays START_INDEX
    if (prev.key === key) return; // top row unchanged: append / edit / optimistic→real swap
    const newIdx = virtualItems.findIndex((it) => it.key === prev.key);
    if (newIdx >= 0) {
      // Older page prepended above the anchor → shift so the anchor stays put.
      // (newIdx < prev.index, i.e. front rows removed, would correctly increase it.)
      setFirstItemIndex((v) => v - (newIdx - prev.index));
    }
    // else: the anchor row vanished while the list is non-empty — the oldest
    // loaded row was deleted (chat/topic SWITCHES reset via the unmount/remount
    // that the `messages.length > 0` render guard forces, not via this branch).
    // Leave firstItemIndex untouched and just adopt the new first row (already
    // stored in prevAnchorRef above); a hard re-base here would inject a phantom
    // front-shift and jump the viewport.
  }, [virtualItems]);

  // Stricter "user is at the absolute bottom" detector for the safety-net
  // effect: `atBottomThreshold={300}` on Virtuoso is generous on purpose
  // so the scroll-to-bottom button appears as soon as the user has
  // scrolled even a few rows up. That threshold is too loose to decide
  // whether to re-anchor on delete — if the user is reading a message
  // ~200px above the bottom (still atBottom by Virtuoso's threshold), we
  // would yank them to the absolute bottom on every delete and they
  // lose context. Maintain our own ref updated from real scroll events
  // with a 50px window, and gate the safety-net on it instead.
  const wasAtAbsoluteBottomRef = useRef(true);
  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return;
    let rafId: number | null = null;
    const measure = () => {
      rafId = null;
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      wasAtAbsoluteBottomRef.current = dist < 50;
    };
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(measure);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    measure(); // capture initial position
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [containerRef]);

  // Bottom-follow polling primitive used by both `forceScrollToBottom` (send /
  // receive) and the safety-net effect (delete). After an initial sync scroll
  // pass, the polling loop re-anchors `scrollTop = scrollHeight` on every
  // animation frame for up to ~1 second, exiting early when the user scrolls
  // away or when scrollHeight has been stable for 3 consecutive frames.
  //
  // Why polling instead of the previous 3-rAF approach? Tall bubbles (voice
  // messages, recordings, link previews with images) resize MULTIPLE times
  // across multiple ticks as their internal async data lands: initial mount
  // → audio metadata loaded (duration shown) → waveform drawn → image
  // decoded. Each resize fires ResizeObserver, and Virtuoso updates its
  // spacers accordingly — but a fixed 3-frame chain only catches the FIRST
  // ResizeObserver round. Subsequent rounds (50–500 ms later) leave the
  // latest message partially cropped under the input bar.
  //
  // The polling loop is cheap: each tick is one scrollHeight read and one
  // (browser-no-op when at bottom) scrollTop assignment. Stable-frame
  // early-exit caps work at ~50 ms for already-stable lists; the 1-second
  // upper bound guards against runaway async (e.g. images that never load).
  const pollingRef = useRef<number | null>(null);
  const startBottomPolling = useCallback(() => {
    if (pollingRef.current !== null) cancelAnimationFrame(pollingRef.current);
    let elapsedFrames = 0;
    let lastHeight = -1;
    let lastSetTop = -1; // last scrollTop value we wrote (after browser clamp)
    let stableFrames = 0;
    const MAX_FRAMES = 60; // ~1 s at 60 fps — runaway-loop guard
    const STABLE_THRESHOLD = 3; // 3 frames with unchanged height = settled
    const USER_SCROLL_PX = 30; // |delta| from our last-set scrollTop that
    // we interpret as the user taking over
    const tick = () => {
      pollingRef.current = null;
      if (++elapsedFrames > MAX_FRAMES) return;
      const el = containerRef?.current;
      if (!el) return;

      // Detect user scroll DIRECTLY by comparing current scrollTop against
      // what we wrote in the previous tick. This avoids the race with
      // `wasAtAbsoluteBottomRef`: scroll events from our own sync
      // `scrollTop = scrollHeight` assignment are dispatched AFTER the
      // first polling tick reads the ref, so a wasAt-based gate would see
      // a stale value and either skip when it shouldn't (race against
      // forceScrollToBottom's intent) or fire when it shouldn't.
      // Comparing scrollTop directly works regardless of scroll-event
      // delivery timing.
      const currentTop = el.scrollTop;
      if (lastSetTop !== -1 && Math.abs(currentTop - lastSetTop) > USER_SCROLL_PX) {
        return; // user scrolled — never yank them back from a deliberate scroll
      }

      const currentHeight = el.scrollHeight;
      if (currentHeight === lastHeight) {
        if (++stableFrames >= STABLE_THRESHOLD) return;
      } else {
        lastHeight = currentHeight;
        stableFrames = 0;
        // `scrollTop = scrollHeight` is browser-clamped to scrollHeight -
        // clientHeight, hitting the true bottom regardless of Virtuoso's
        // internal height estimates. Idempotent when already at the
        // bottom (no scroll event fires, no extra layout work).
        el.scrollTop = currentHeight;
        lastSetTop = el.scrollTop; // capture the post-clamp value the
        // browser actually committed to
      }
      pollingRef.current = requestAnimationFrame(tick);
    };
    pollingRef.current = requestAnimationFrame(tick);
  }, [containerRef]);

  // Force-scroll to the absolute bottom. Used by parent's send / receive /
  // Android-focus handlers via `scrollToBottomRef`.
  //
  // Sync pass: tells Virtuoso to un-virtualize the last item (its DOM node
  // may not exist when called from the top of a long chat) and lands the
  // scroller approximately. Subsequent ticks of `startBottomPolling`
  // re-anchor as the bubble's real height settles via ResizeObserver.
  const forceScrollToBottom = useCallback(() => {
    const idx = virtualItemsLenRef.current - 1;
    if (idx >= 0) {
      virtuosoRef.current?.scrollToIndex({ index: idx, align: "end", behavior: "auto" });
    }
    const el = containerRef?.current;
    if (el) el.scrollTop = el.scrollHeight;
    startBottomPolling();
  }, [containerRef, startBottomPolling]);

  useEffect(() => {
    if (!scrollToBottomRef) return;
    scrollToBottomRef.current = forceScrollToBottom;
    // Clear on unmount / re-registration so a stale closure can't fire
    // against a unmounted scroller (or a scroller the parent re-mounted
    // for a different chat instance using the same ref object).
    return () => {
      scrollToBottomRef.current = null;
    };
  }, [scrollToBottomRef, forceScrollToBottom]);

  // Safety net for delete-near-bottom: when the visible item list mutates
  // in a way that can shift the tail (item count changes OR the last item's
  // key changes) while the user was at the bottom, start bottom-follow
  // polling. Virtuoso's `followOutput="auto"` only fires for appended
  // output — it does nothing on removal, so without us nothing keeps the
  // viewport pinned to the new bottom after a delete.
  //
  // Tracking the (length|lastKey) pair covers both:
  //   - "deleted the last message" — lastKey changes
  //   - "deleted a message before the last" — length changes, lastKey same
  // Edits and optimistic→real resolves don't change either, so the effect
  // correctly skips them. Rapid back-to-back mutations (multi-select bulk
  // delete) re-trigger `startBottomPolling` which cancels the in-flight
  // rAF and starts a fresh polling window — only the latest mutation's
  // 1-second watch matters.
  const lastListSigRef = useRef<string | null>(null);
  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];
    const lastKey = lastItem ? lastItem.key : "";
    const sig = `${virtualItems.length}|${lastKey}`;
    const prevSig = lastListSigRef.current;
    lastListSigRef.current = sig;

    // Skip the initial mount: no previous signature means we just rendered
    // the first batch and initialTopMostItemIndex already aligned us.
    if (prevSig === null) return;
    if (sig === prevSig) return;
    if (virtualItems.length === 0) return;
    // Strict 50px detector captures pre-mutation position. Loose Virtuoso
    // `atBottomThreshold={300}` would yank the user to the absolute bottom
    // even when they were intentionally reading a row or two above it.
    if (!wasAtAbsoluteBottomRef.current) return;

    startBottomPolling();
  }, [virtualItems, startBottomPolling]);

  // PASSIVE follow-bottom watcher: keeps the viewport pinned to the true
  // bottom for as long as the user IS at the bottom, regardless of what
  // caused the content height to change. Covers the long tail that
  // `startBottomPolling` misses — its 1 s cap can't follow images that
  // take 2–10 s to load on slow networks.
  //
  // Specifically: inline image bubbles (MessageBubble:763) render an
  // empty `<img loading="lazy">` whose height jumps from 0 to ~300 px
  // when the image decodes — sometimes seconds later. Voice bubble
  // waveforms, recording posters, and link-preview thumbnails behave
  // similarly. Each fires ResizeObserver on the bubble; Virtuoso
  // updates its spacer; scrollHeight grows. Without this watcher,
  // viewport drifts off the bottom.
  //
  // Gates that protect the user's deliberate scroll:
  //   - `wasAtAbsoluteBottomRef` (50 px window, updated from scroll
  //     events) — only follow if user IS at the bottom right now.
  //   - 200 ms grace after the last user-initiated scroll event — never
  //     override a deliberate scroll that just happened.
  //   - `dist > 4` — idempotent no-op when already at the bottom.
  const resizeWatcherRafRef = useRef<number | null>(null);
  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return;
    // Tracks the most recent scroll event of ANY origin — user wheel/touch,
    // momentum continuation, AND scrolls caused by OTHER programmatic
    // writers (polling, forceScrollToBottom, scrollToIndex(smooth),
    // useKeepScrollPosition). Load-bearing for serialisation: the watcher's
    // grace check below gates it out for the entire duration of any other
    // active scroll-anchor flow, eliminating double-writes without manual
    // coordination flags. DO NOT filter to "user-initiated" only.
    //
    // BUT we DO distinguish our OWN writes via `selfWriteAt` below — when
    // the watcher itself writes scrollTop, we don't want the resulting
    // scroll event to refresh the grace and starve the next tick of a
    // slowly-growing async load (e.g. a 2-s image decode that fires
    // ResizeObserver in 100 ms ticks). Without this, repeated resize
    // events would each be silenced by the watcher's own previous write.
    let lastScrollEventAt = 0;
    let selfWriteAt = 0;
    const onScrollEvent = () => {
      // Ignore scroll events caused by our own scrollTop write — we know
      // we just wrote, no need to refresh the grace for ourselves.
      if (Date.now() - selfWriteAt < 50) return;
      lastScrollEventAt = Date.now();
    };
    el.addEventListener("scroll", onScrollEvent, { passive: true });

    const onResize = () => {
      if (resizeWatcherRafRef.current !== null) return;
      resizeWatcherRafRef.current = requestAnimationFrame(() => {
        resizeWatcherRafRef.current = null;
        // Cache→fresh settle window (1.5 s after parent calls
        // settleAtBottomRef): AGGRESSIVE bottom-pinning. Skip the 500 ms
        // scroll-event grace, skip the selfWriteAt suppression, and force
        // scrollTop = scrollHeight on EVERY resize fire while the user was
        // at the bottom. This is the only path that survives the async
        // load cascade right after page-reload, where polling exits at 1 s
        // but image / link-preview / voice loads keep growing scrollHeight
        // for another 0.5–1 s. Highlight settle still wins (rare to coincide).
        if (Date.now() < cacheToFreshSettleUntilRef.current) {
          if (wasAtAbsoluteBottomRef.current && Date.now() >= highlightSettleUntilRef.current) {
            const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (dist > 4) {
              selfWriteAt = Date.now();
              el.scrollTop = el.scrollHeight;
            }
          }
          return;
        }
        // 500 ms grace covers Mac trackpad momentum (events can be sparse
        // at the deceleration tail, occasionally > 200 ms between ticks)
        // and other writers' active windows (polling, forceScrollToBottom).
        if (Date.now() - lastScrollEventAt < 500) return;
        // Highlight (search / media-gallery jump) recently positioned the
        // viewport on a specific message — don't override that for ~1.5 s.
        if (Date.now() < highlightSettleUntilRef.current) return;
        if (!wasAtAbsoluteBottomRef.current) return;
        const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (dist > 4) {
          selfWriteAt = Date.now();
          el.scrollTop = el.scrollHeight;
        }
      });
    };

    const ro = new ResizeObserver(onResize);
    const observed = new WeakSet<Element>();
    const observeAll = () => {
      for (const child of Array.from(el.children)) {
        if (!observed.has(child)) {
          ro.observe(child);
          observed.add(child);
        }
      }
    };
    observeAll();
    // Re-observe whenever Virtuoso adds/removes nodes anywhere inside the
    // scroller. `subtree: true` is required because in react-virtuoso 4.x
    // the scroller's direct child is a single viewport wrapper
    // (`data-virtuoso-scroller`/`data-viewport-type=element`); the actual
    // item bubbles mount deeper inside it. With `subtree: false` we'd
    // observe only the wrapper — its size still reflects total content
    // height, but item-level resize signals would be invisible to the
    // MutationObserver and any future Virtuoso DOM layout change could
    // silently break the watcher. The WeakSet dedup keeps the observed
    // set bounded as items churn during scroll.
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.removedNodes.forEach((n) => {
          if (n instanceof Element && observed.has(n)) {
            ro.unobserve(n);
            observed.delete(n);
          }
        });
        m.addedNodes.forEach((n) => {
          if (n instanceof Element && !observed.has(n)) {
            ro.observe(n);
            observed.add(n);
          }
        });
      }
    });
    mo.observe(el, { childList: true, subtree: true });

    return () => {
      if (resizeWatcherRafRef.current !== null) {
        cancelAnimationFrame(resizeWatcherRafRef.current);
        resizeWatcherRafRef.current = null;
      }
      el.removeEventListener("scroll", onScrollEvent);
      ro.disconnect();
      mo.disconnect();
    };
  }, [containerRef]);

  // Cancel any pending rAF / setTimeout on unmount so callbacks don't fire
  // against detached refs (or, in pathological cases where the parent re-uses
  // the same `containerRef` for a new chat instance, against a scroller that
  // now belongs to a different chat).
  const pendingButtonTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (pollingRef.current !== null) {
        cancelAnimationFrame(pollingRef.current);
        pollingRef.current = null;
      }
      if (resizeWatcherRafRef.current !== null) {
        cancelAnimationFrame(resizeWatcherRafRef.current);
        resizeWatcherRafRef.current = null;
      }
      if (pendingButtonTimerRef.current !== null) {
        clearTimeout(pendingButtonTimerRef.current);
        pendingButtonTimerRef.current = null;
      }
    },
    []
  );

  // Set when the highlight effect commits a scrollToIndex — used by the
  // ResizeObserver watcher to skip re-anchoring for ~1.5 s afterwards.
  // Without this guard, if the highlight lands the viewport within the
  // strict 50 px wasAt window (e.g. a near-tail message centered), and an
  // image above decodes shortly after, the watcher would yank the viewport
  // past the highlighted message to the absolute bottom, defeating the
  // deep-link / search-jump UX.
  const highlightSettleUntilRef = useRef(0);

  // Set when the parent triggers a cache→fresh phase swap (after page
  // reload + initial fetch). During this 1.5 s window the watcher is in
  // AGGRESSIVE mode: it ignores the normal 500 ms scroll-event grace and
  // the selfWriteAt suppression, and force-pins scrollTop = scrollHeight
  // on EVERY ResizeObserver fire if the user was at the bottom. This
  // absorbs the cascade of async height changes (image decodes, link
  // preview swaps, voice metadata, etc.) that would otherwise leave the
  // user drifting slightly above the absolute bottom by the time the
  // last load lands after polling's 1 s window has exited.
  const cacheToFreshSettleUntilRef = useRef(0);
  useEffect(() => {
    if (!settleAtBottomRef) return;
    settleAtBottomRef.current = () => {
      cacheToFreshSettleUntilRef.current = Date.now() + 1500;
      forceScrollToBottom();
    };
    return () => {
      settleAtBottomRef.current = null;
    };
  }, [settleAtBottomRef, forceScrollToBottom]);
  useEffect(() => {
    if (!highlightMessageId) return;
    const idx = virtualItems.findIndex((item) => item.type === "bubble" && item.message.id === highlightMessageId);
    if (idx >= 0) {
      virtuosoRef.current?.scrollToIndex({ index: idx, behavior: "smooth", align: "center" });
      highlightSettleUntilRef.current = Date.now() + 1500;
    }
  }, [highlightMessageId, virtualItems]);

  const handleStartReached = useCallback(() => {
    if (infiniteScrollEnabled && loadMore) {
      loadMore(() => {});
    }
  }, [infiniteScrollEnabled, loadMore]);

  const renderItem = useCallback(
    (_index: number, item: VListItem) => {
      switch (item.type) {
        case "date":
          return <DateSeparator date={item.date} />;
        case "unread":
          return (
            <div className="messages-list-view__new-message">
              <div className="messages-list-view__new-message-content small">{t("New messages")}</div>
            </div>
          );
        case "system":
          return (
            <SystemMessageCard
              text={item.text}
              type={item.systemType}
              activeCallUuid={item.activeCallUuid}
              isLastCallStart={item.isLastCallStart}
              onOpenInsights={handleOpenInsights}
            />
          );
        case "livechat": {
          const MessageItem = getMessageItem(item.message);
          if (!MessageItem) return <div />;
          return (
            <MessageItem
              message={item.message}
              className={messageItemClass}
              rendered={messageObserve}
              avatarSize={avatarSize}
              showScreenName={showScreenName}
              isLiveChat={isLiveChat}
              startMessage={item.startMessage}
              endMessage={item.endMessage}
              authUser={user}
              guestUser={guestUser}
              screenName={userNames ? userNames[item.message.senderId] : undefined}
            />
          );
        }
        case "bubble":
          return (
            <div
              className={item.isHighlighted ? "messages-list-view__highlight" : undefined}
              data-message-id={item.message.id}
            >
              <MessageBubble
                message={item.message}
                authUser={user}
                isOwn={item.isOwn}
                showAvatar={item.endMessage && showScreenName}
                showName={item.startMessage && showScreenName}
                isStart={item.startMessage}
                isEnd={item.endMessage}
                rendered={messageObserve}
                onMessageUpdated={onMessageUpdated}
                onMessageDeleted={onMessageDeleted}
                opponentMinUnreadMessageId={opponentMinUnreadMessageId}
                searchQuery={searchQuery}
                onReply={onReply}
                onForward={onForward}
                onPin={onPin}
                onOpenThread={onOpenThread}
                onQuoteClick={onQuoteClick}
                threadReplyCount={threadReplyCounts?.get(item.message.id)}
                isPinned={pinnedMessageIds?.has(item.message.id)}
                canModerate={canModerate}
                selectionMode={selectionMode}
                selected={selectedIds?.has(item.message.id)}
                onToggleSelection={onToggleSelection}
                onEnterSelection={onEnterSelection}
              />
            </div>
          );
        default:
          return <div />;
      }
    },
    [
      t,
      messageItemClass,
      messageObserve,
      avatarSize,
      showScreenName,
      isLiveChat,
      user,
      guestUser,
      userNames,
      onMessageUpdated,
      onMessageDeleted,
      opponentMinUnreadMessageId,
      searchQuery,
      onReply,
      onForward,
      onPin,
      onOpenThread,
      onQuoteClick,
      threadReplyCounts,
      pinnedMessageIds,
      canModerate,
      selectionMode,
      selectedIds,
      onToggleSelection,
      onEnterSelection,
      handleOpenInsights,
    ]
  );

  return (
    <div className={cls.join(" ")} style={{ position: "relative" }}>
      <Virtuoso
        ref={virtuosoRef}
        scrollerRef={(el) => {
          if (containerRef) containerRef.current = el as HTMLDivElement | null;
        }}
        className="messages-list-view__scroll"
        data={virtualItems}
        firstItemIndex={firstItemIndex}
        itemContent={(index, item) => renderItem(index, item)}
        computeItemKey={(_index, item) => item.key}
        initialTopMostItemIndex={virtualItems.length - 1}
        followOutput="auto"
        alignToBottom
        startReached={handleStartReached}
        atBottomStateChange={setAtBottom}
        atBottomThreshold={300}
        defaultItemHeight={80}
        overscan={400}
        // Breathing room between the last bubble and the message input —
        // the Virtuoso scroller ignores the container's padding, so the
        // spacer has to be a list footer.
        components={LIST_COMPONENTS}
      />
      {!isLiveChat && (
        <ScrollToBottomButton
          visible={!atBottom}
          unreadCount={unreadCount}
          onClick={() => {
            const el = containerRef?.current;
            if (!el) return;
            // Smooth scroll to current scrollHeight. As the animation runs,
            // Virtuoso renders items into view (its scroll listener picks
            // up the progression events) and ResizeObserver may fire as
            // those items measure — scrollHeight can grow during the
            // animation, especially for chats with tall bubbles (voice
            // messages, recordings, link previews). After the smooth
            // animation settles (~550 ms), kick off the bottom-follow
            // polling so any continued size changes (image decode, audio
            // metadata) keep re-anchoring scrollTop to the true bottom.
            const target = el.scrollHeight;
            el.scrollTo({ top: target, behavior: "smooth" });
            if (pendingButtonTimerRef.current !== null) clearTimeout(pendingButtonTimerRef.current);
            pendingButtonTimerRef.current = setTimeout(() => {
              pendingButtonTimerRef.current = null;
              const el2 = containerRef?.current;
              if (!el2) return;
              // Skip if the user clearly took over scrolling during the
              // animation (|delta from original target| > 100 px) — never
              // yank them back from a deliberate manual scroll.
              if (Math.abs(el2.scrollTop - target) > 100) return;
              startBottomPolling();
            }, 550);
          }}
          hasMention={hasMention}
          onMentionClick={onMentionClick}
        />
      )}
      {insightsRecordingId && <InsightsModal recordingId={insightsRecordingId} onClose={handleCloseInsights} />}
    </div>
  );
}

export default MessagesListView;
